import type { ComponentType, FC, ReactNode } from 'react';
import type { RuleSet } from 'styled-components';

import dynamic from 'next/dynamic';
import { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { WashingCareIcon } from './WashingCareIcon';
import type { IconProps } from '~/components/shared/core/icons';
import { Typography } from '~/components/shared/core/typography/Typography';
import { useLayoutDesignContext } from '~/utilities/context/dynamic/LayoutDesignContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import type { Maybe, WashingLabel } from '~/utilities/graphql/codegen';
import { SiteType } from '~/utilities/graphql/codegen';
import { toKebabCase, toPascalCase } from '~/utilities/parsers';

const WahtsWashingLabelColorStyle = css`
  color: ${({ theme }) => theme.colors.content.secondary};
`;

const WashingLabelColorConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Employeeshop]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Wahts]: WahtsWashingLabelColorStyle,
};

const S = {
  IconsContainer: styled.ul`
    display: flex;
    justify-content: flex-start;
    margin-top: 8px;
    margin-bottom: 0;
    padding-left: 0;
  `,

  WashingInstruction: styled(Typography)<{ $hasProductAccordions?: boolean }>`
    margin: 2px 0 13px;
    ${({ $hasProductAccordions }) =>
      !$hasProductAccordions &&
      css`
        height: 80px;
        min-height: 60px;
      `};
    line-height: 20px;
    word-break: break-all;
  `,

  WashingLabel: styled.span`
    display: none;
    color: ${({ theme }) => theme.colors.content.tertiary};
    font-size: ${({ theme }) => theme.font.size['3']};

    ${({ theme }) => WashingLabelColorConfig[theme.siteType]}

    &[data-active='true'] {
      display: initial;
    }
  `,
};

export const WashingLabelsList: FC = () => {
  const [hoverIndex, setHoverIndex] = useState(0);
  const { hasProductAccordions } = useLayoutDesignContext();

  const mouseEnter = useCallback((index: number) => {
    setHoverIndex(index);
  }, []);

  const mouseLeave = useCallback(() => {
    setHoverIndex(0);
  }, []);

  const {
    product: { washingLabels },
  } = usePDPContext();

  const washingLabelsWithIcons: Maybe<
    Array<Maybe<WashingLabel> & { icon: Maybe<ComponentType<IconProps>> }>
  > = useMemo(() => {
    if (!washingLabels) {
      return [];
    }

    return washingLabels.map(washingLabel => ({
      ...washingLabel,
      icon: washingLabel?.iconCssClass
        ? dynamic(
            async () =>
              import(`../../shared/core/icons/${toPascalCase(washingLabel.iconCssClass || '')}`)
          )
        : null,
    }));
  }, [washingLabels]);

  const washingIcons = new Array<ReactNode>();
  const labels = new Array<ReactNode>();

  washingLabelsWithIcons.forEach(({ icon: CareIcon, label, iconCssClass }, index) => {
    if (CareIcon) {
      washingIcons.push(
        <WashingCareIcon
          key={`${iconCssClass}-${index}`}
          CareIcon={CareIcon}
          label={label}
          index={index}
          isSelected={hoverIndex === index}
          hasProductAccordions={hasProductAccordions}
          mouseEnter={mouseEnter}
          mouseLeave={mouseLeave}
        />
      );
    }

    labels.push(
      <S.WashingLabel
        data-active={hoverIndex === index}
        key={`${iconCssClass}-${index}`}
        id={`washing-label-${index}-${toKebabCase(label || '')}`}
      >
        {label}
      </S.WashingLabel>
    );
  });

  return (
    <>
      <S.IconsContainer>{washingIcons}</S.IconsContainer>
      <S.WashingInstruction
        testId="washing-instruction"
        component="div"
        variant="body"
        $hasProductAccordions={hasProductAccordions}
      >
        {labels}
      </S.WashingInstruction>
    </>
  );
};
